import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlay, FaYoutube, FaNewspaper, FaPodcast, 
  FaExternalLinkAlt, FaCalendar, FaClock, FaUser,
  FaMicrophone, FaVideo, FaFileAlt
} from 'react-icons/fa';
import { mediaContent } from '../data/portfolioData';
import AnimatedSection from './AnimatedSection';
import { useTranslation } from 'react-i18next';
import { localizedMedia } from '../locales/contentTranslations';

const Media = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('videos');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const tabs = [
    { id: 'videos', label: t('media.videos'), icon: FaVideo },
    { id: 'articles', label: t('media.articles'), icon: FaNewspaper },
    { id: 'podcasts', label: t('media.podcasts'), icon: FaPodcast }
  ];

  const currentContent = mediaContent[activeTab].map(item => localizedMedia(activeTab, item, i18n));


  return (
    <section id="media" className="py-32 relative">
      <div className="container mx-auto px-4">
        <AnimatedSection>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            {t("media.title1")} <span className="text-gradient">{t("media.title2")}</span>
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            {t("media.subtitle")}
          </p>
        </AnimatedSection>

        {/* Tabovi */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg'
                    : 'glass-effect text-gray-300 hover:text-white'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Video sekcija */}
        {activeTab === 'videos' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {currentContent.map((video, index) => (
              <AnimatedSection key={video.id}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className="glass-effect rounded-2xl overflow-hidden group cursor-pointer"
                  onClick={() => setSelectedVideo(video)}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                        <FaPlay className="text-white text-2xl ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-sm">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2 bg-primary px-3 py-1 rounded-full text-sm">
                      {video.type}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                      <FaYoutube className="text-red-500" />
                      <span>{video.platform}</span>
                      <span>•</span>
                      <FaCalendar />
                      <span>{video.date}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition">
                      {video.title}
                    </h3>
                    <p className="text-gray-400 line-clamp-2">{video.description}</p>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        )}

        {/* Članci sekcija */}
        {activeTab === 'articles' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {currentContent.map((article, index) => (
              <AnimatedSection key={article.id}>
                <motion.a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -5 }}
                  className="glass-effect rounded-2xl overflow-hidden block group"
                >
                  <div className="h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <FaNewspaper className="text-6xl text-primary/50" />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 flex-wrap">
                      <span className="px-2 py-1 glass-effect rounded-full text-xs">
                        {article.category}
                      </span>
                      <span>•</span>
                      <FaCalendar className="text-xs" />
                      <span>{article.date}</span>
                      <span>•</span>
                      <FaUser className="text-xs" />
                      <span>{article.author}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition">
                      {article.title}
                    </h3>
                    <p className="text-gray-400 mb-4 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-primary">{article.publication}</span>
                      <span className="text-primary group-hover:translate-x-1 transition">
                        {t("media.readMore")} →
                      </span>
                    </div>
                  </div>
                </motion.a>
              </AnimatedSection>
            ))}
          </div>
        )}

        {/* Podcast sekcija */}
        {activeTab === 'podcasts' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {currentContent.map((podcast, index) => (
              <AnimatedSection key={podcast.id}>
                <div className="glass-effect rounded-2xl p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <FaMicrophone className="text-2xl text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                        <span className="px-2 py-1 glass-effect rounded-full text-xs">
                          {podcast.platform}
                        </span>
                        <span>•</span>
                        <FaClock />
                        <span>{podcast.duration}</span>
                        <span>•</span>
                        <FaCalendar />
                        <span>{podcast.date}</span>
                      </div>
                      <h3 className="text-xl font-bold mb-2">{podcast.title}</h3>
                      <p className="text-gray-400 mb-4">{podcast.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <a
                      href={podcast.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-primary to-secondary rounded-lg text-center font-semibold hover-glow transition"
                    >
                      {t("media.listenOn")} {podcast.platform}
                    </a>
                    {podcast.embedUrl && (
                      <button
                        onClick={() => setSelectedVideo(podcast)}
                        className="px-4 py-2 glass-effect rounded-lg hover:bg-white/10 transition"
                      >
                        <FaPlay />
                      </button>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        )}
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl bg-darker rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedVideo(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 glass-effect rounded-full flex items-center justify-center hover:bg-white/10 transition"
              >
                ✕
              </button>
              
              <div className="aspect-video">
                <iframe
                  src={selectedVideo.url}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-2">{selectedVideo.title}</h3>
                <p className="text-gray-400">{selectedVideo.description}</p>
                <div className="flex gap-4 mt-4 text-sm text-gray-500">
                  <span>{selectedVideo.date}</span>
                  <span>{selectedVideo.duration}</span>
                  <span>{selectedVideo.platform}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Media;